import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Upload, FileSpreadsheet, Scale, CheckCircle2, AlertTriangle, ArrowDownCircle,
  ArrowUpCircle, HelpCircle, Link2, ChevronDown, ChevronRight, Download,
  Wand2, Save, RotateCcw, History, Boxes, ArrowRight, Flag, StickyNote, Lock, Unlock,
  Undo2, Search, Receipt, ArrowRightLeft, X, ExternalLink, Layers, Plus, Trash2, Star,
} from "lucide-react";
import SafetyShell from "../safety/SafetyShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const fmtDate = (s) => {
  if (!s) return "—";
  const hasTime = s.includes("T");
  const d = new Date(hasTime ? s : `${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleString("en-IN", hasTime
    ? { timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }
    : { timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit" });
};

const STATUS = {
  match: { label: "Match", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  erp_short: { label: "ERP short", icon: ArrowUpCircle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  erp_excess: { label: "ERP excess", icon: ArrowDownCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  unmapped: { label: "Unmapped", icon: HelpCircle, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  not_counted: { label: "Not counted", icon: AlertTriangle, cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

function Kpi({ icon: Icon, label, value, tone = "zinc", testid, onClick, active }) {
  const tones = {
    zinc: "text-zinc-700 bg-zinc-50", emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50", rose: "text-rose-700 bg-rose-50",
    violet: "text-violet-700 bg-violet-50", sky: "text-sky-700 bg-sky-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`sf-surface p-4 text-left transition-all ${onClick ? "hover:shadow-md cursor-pointer" : "cursor-default"} ${active ? "ring-2 ring-emerald-500" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
        <span className={`p-1 rounded ${tones[tone]}`}><Icon className="w-3.5 h-3.5" /></span>
        {label}
      </div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
    </button>
  );
}

export default function InventoryReconcile({ user, onLogout }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [asOf, setAsOf] = useState(new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { items, summary, ... }
  const [reconId, setReconId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [trueBusy, setTrueBusy] = useState(false);
  const [filter, setFilter] = useState("variances");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState("draft");
  const [noteDraft, setNoteDraft] = useState({}); // { key: text }
  const [rowBusy, setRowBusy] = useState({}); // { key: bool }
  const [catalog, setCatalog] = useState([]);
  const [linkDialog, setLinkDialog] = useState({ open: false, row: null });
  const [linkSearch, setLinkSearch] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [ledger, setLedger] = useState({ open: false, loading: false, data: null, row: null, view: "source" });

  const openLedger = async (it) => {
    setLedger({ open: true, loading: true, data: null, row: it, view: "source" });
    try {
      const res = await axios.get(`${API}/inventory/reconciliation-ledger`, {
        ...auth(),
        params: { product_code: it.product_code, as_of_date: result?.as_of_date || asOf },
      });
      setLedger((l) => ({ ...l, loading: false, data: res.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load ledger");
      setLedger((l) => ({ ...l, open: false, loading: false }));
    }
  };

  const openGroupLedger = async (it) => {
    setLedger({ open: true, loading: true, data: null,
      row: { product_code: it.product_code, description: `Combined · ${(it.member_codes || []).join(" + ")}` },
      view: "source" });
    try {
      const res = await axios.get(`${API}/inventory/reconciliation-group-ledger`, {
        ...auth(),
        params: { group_id: it.group_id, as_of_date: result?.as_of_date || asOf },
      });
      setLedger((l) => ({ ...l, loading: false, data: res.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load group ledger");
      setLedger((l) => ({ ...l, open: false, loading: false }));
    }
  };

  const openReport = (rid) => { if (rid) navigate(`/safety/production-reports/${rid}`); };

  const openPalletCard = async (palletId) => {
    if (!palletId) return;
    try {
      const res = await axios.get(`${API}/pallet/${palletId}/card.pdf?t=${Date.now()}`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open pallet card");
    }
  };

  const openPackingList = (plId) => {
    if (!plId) { toast.error("No packing list linked to this line"); return; }
    const token = localStorage.getItem("token");
    window.open(`${API}/packing-lists/${plId}/preview?token=${encodeURIComponent(token)}`, "_blank", "noopener");
  };

  const exportLedgerCsv = () => {
    const d = ledger.data;
    if (!d) return;
    const rows = [["Timestamp", "Type", "Reference", "Detail", "PO", "Qty", "Running balance"]];
    (d.timeline || []).forEach((e) => {
      rows.push([e.ts || "", e.type, e.ref ?? "", (e.detail || "").replace(/"/g, "'"), e.po_number || "", e.qty ?? "", e.balance ?? ""]);
    });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ledger_${d.product_code}_${(d.as_of_date || "").slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadCatalog = async () => {
    try {
      const r = await axios.get(`${API}/catalog/products`, auth());
      setCatalog(r.data.products || r.data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadCatalog(); }, []);

  // ── Count-as-one groups (reconciliation-only product merging) ──
  const [groups, setGroups] = useState([]);
  const [groupsDialog, setGroupsDialog] = useState(false);
  const [gEditing, setGEditing] = useState(null); // group being edited, or null
  const [gName, setGName] = useState("");
  const [gMembers, setGMembers] = useState([]); // [product_id]
  const [gPrimary, setGPrimary] = useState(null);
  const [gSearch, setGSearch] = useState("");
  const [gBusy, setGBusy] = useState(false);

  const loadGroups = async () => {
    try {
      const r = await axios.get(`${API}/inventory/reconciliation-groups`, auth());
      setGroups(r.data.items || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadGroups(); }, []);

  const [suggestions, setSuggestions] = useState([]);
  const loadSuggestions = async () => {
    try {
      const r = await axios.get(`${API}/inventory/reconciliation-groups/suggestions`, auth());
      setSuggestions(r.data.suggestions || []);
    } catch { setSuggestions([]); }
  };
  const openGroupsDialog = () => { resetGroupForm(); loadSuggestions(); setGroupsDialog(true); };
  const createFromSuggestion = async (sug) => {
    setGBusy(true);
    try {
      await axios.post(`${API}/inventory/reconciliation-groups`, {
        name: sug.base_code,
        product_ids: sug.members.map((m) => m.product_id),
        primary_product_id: sug.primary_product_id,
      }, auth());
      toast.success(`Grouped "${sug.base_code}" (${sug.members.length} codes)`);
      await Promise.all([loadGroups(), loadSuggestions()]);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create group"); }
    finally { setGBusy(false); }
  };

  const resetGroupForm = () => { setGEditing(null); setGName(""); setGMembers([]); setGPrimary(null); setGSearch(""); };
  const startEditGroup = (g) => {
    setGEditing(g); setGName(g.name || "");
    setGMembers(g.member_product_ids || []);
    setGPrimary(g.primary_product_id || null); setGSearch("");
  };
  const toggleMember = (pid) => {
    setGMembers((prev) => {
      const has = prev.includes(pid);
      const next = has ? prev.filter((x) => x !== pid) : [...prev, pid];
      if (has && gPrimary === pid) setGPrimary(next[0] || null);
      if (!has && !gPrimary) setGPrimary(pid);
      return next;
    });
  };
  const saveGroup = async () => {
    if (gMembers.length < 2) { toast.error("Pick at least 2 products to group"); return; }
    setGBusy(true);
    try {
      const body = { name: gName.trim() || "Group", product_ids: gMembers, primary_product_id: gPrimary };
      if (gEditing) await axios.put(`${API}/inventory/reconciliation-groups/${gEditing.id}`, body, auth());
      else await axios.post(`${API}/inventory/reconciliation-groups`, body, auth());
      toast.success(gEditing ? "Group updated" : "Group created");
      resetGroupForm();
      await Promise.all([loadGroups(), loadSuggestions()]);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save group"); }
    finally { setGBusy(false); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Its members will reconcile separately again.`)) return;
    try {
      await axios.delete(`${API}/inventory/reconciliation-groups/${g.id}`, auth());
      toast.success("Group deleted");
      if (gEditing?.id === g.id) resetGroupForm();
      await Promise.all([loadGroups(), loadSuggestions()]);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete group"); }
  };
  const gCatalogFiltered = useMemo(() => {
    const q = gSearch.trim().toLowerCase();
    let list = catalog;
    if (q) list = list.filter((p) => (`${p.product_code} ${p.description || ""}`).toLowerCase().includes(q));
    return list.slice(0, 60);
  }, [catalog, gSearch]);


  // Re-run reconciliation to reflect the current ERP/catalog state.
  const refresh = async () => {
    if (reconId) {
      const r = await axios.get(`${API}/inventory/reconciliation/${reconId}?recompute=1`, auth());
      setResult(r.data.result);
      setStatus(r.data.status || status);
    } else if (file) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("as_of_date", asOf);
      const r = await axios.post(`${API}/inventory/reconciliation/preview`, fd, auth());
      setResult(r.data);
    }
  };

  const undoTrueUp = async (row) => {
    if (!reconId) return;
    try {
      const r = await axios.post(`${API}/inventory/reconciliation/${reconId}/undo-true-up`,
        { product_codes: row.is_group ? [row.primary_code, ...(row.member_codes || [])] : [row.product_code] }, auth());
      setResult(r.data.result);
      toast.success(`Removed true-up adjustment for ${row.product_code}`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  };

  const openLinkDialog = (row) => { setLinkDialog({ open: true, row }); setLinkSearch(""); };

  const submitLink = async (product) => {
    const row = linkDialog.row;
    if (!row || !product) return;
    setLinkBusy(true);
    try {
      await axios.post(`${API}/inventory/map-product`,
        { raw_text: row.product_code, target_product_code: product.product_code }, auth());
      toast.success(`Linked ${row.product_code} → ${product.product_code}`);
      setLinkDialog({ open: false, row: null });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setLinkBusy(false); }
  };

  // Apply an updated annotation to the in-memory result items.
  const applyAnnotation = (key, patch) => {
    setResult((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((i) => (i.key === key ? { ...i, ...patch } : i));
      const flagged = items.filter((i) => i.flagged).length;
      return { ...prev, items, summary: { ...prev.summary, flagged } };
    });
  };

  const setFlag = async (row, flagged) => {
    if (!reconId) { toast.error("Save the reconciliation first to flag rows"); return; }
    applyAnnotation(row.key, { flagged });
    try {
      await axios.patch(`${API}/inventory/reconciliation/${reconId}/annotation`,
        { key: row.key, flagged }, auth());
    } catch {
      applyAnnotation(row.key, { flagged: !flagged });
      toast.error("Could not update flag");
    }
  };

  const saveNote = async (row) => {
    if (!reconId) { toast.error("Save the reconciliation first to add notes"); return; }
    const note = noteDraft[row.key] ?? row.note ?? "";
    setRowBusy((b) => ({ ...b, [row.key]: true }));
    try {
      await axios.patch(`${API}/inventory/reconciliation/${reconId}/annotation`,
        { key: row.key, note }, auth());
      applyAnnotation(row.key, { note });
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    } finally {
      setRowBusy((b) => ({ ...b, [row.key]: false }));
    }
  };

  const toggleStatus = async () => {
    if (!reconId) return;
    const next = status === "final" ? "draft" : "final";
    try {
      await axios.post(`${API}/inventory/reconciliation/${reconId}/status`, { status: next }, auth());
      setStatus(next);
      toast.success(next === "final" ? "Marked as final" : "Reopened as draft");
      loadHistory();
    } catch { toast.error("Could not change status"); }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/inventory/reconciliation`, auth());
      setHistory(r.data.items || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadHistory(); }, []);

  // Auto-open a reconciliation passed via ?recon=<id> (e.g. from a finalized scan count).
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("recon");
    if (rid) openSaved(rid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    if (!file) { toast.error("Choose your physical stock file first"); return; }
    setBusy(true); setResult(null); setReconId(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("as_of_date", asOf);
      const r = await axios.post(`${API}/inventory/reconciliation/preview`, fd, auth());
      setResult(r.data);
      setFilter("variances");
      toast.success(`Reconciled ${r.data.parsed_count} products · ${r.data.summary.variances} variance(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciliation failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("as_of_date", asOf);
      const r = await axios.post(`${API}/inventory/reconciliation`, fd, auth());
      setReconId(r.data.id);
      setResult(r.data.result);
      setStatus(r.data.status || "draft");
      toast.success("Reconciliation saved as draft — flag rows, add notes, true-up & export");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const openSaved = async (id) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/inventory/reconciliation/${id}?recompute=1`, auth());
      setReconId(id);
      setResult(r.data.result);
      setStatus(r.data.status || "draft");
      setAsOf((r.data.as_of_date || "").slice(0, 16));
      setFilter("variances");
    } catch { toast.error("Could not load reconciliation"); }
    finally { setBusy(false); }
  };

  const trueUp = async (codes, all = false) => {
    if (!reconId) { toast.error("Save the reconciliation first to enable true-up"); return; }
    setTrueBusy(true);
    try {
      const r = await axios.post(`${API}/inventory/reconciliation/${reconId}/true-up`,
        { product_codes: codes || [], all_variances: all, as_of_date: asOf }, auth());
      setResult(r.data.result);
      toast.success(`Posted ${r.data.created_count} adjustment(s) — ERP now matches physical`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "True-up failed");
    } finally { setTrueBusy(false); }
  };

  const exportXlsx = async () => {
    if (!reconId) { toast.error("Save the reconciliation to export"); return; }
    try {
      const r = await axios.get(`${API}/inventory/reconciliation/${reconId}/export.xlsx?recompute=1`,
        { ...auth(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `reconciliation_${asOf}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const [drillExporting, setDrillExporting] = useState(false);
  const exportDrilldownXlsx = async () => {
    if (!reconId) { toast.error("Save the reconciliation to export"); return; }
    setDrillExporting(true);
    try {
      const r = await axios.get(`${API}/inventory/reconciliation/${reconId}/export-drilldown.xlsx?recompute=1`,
        { ...auth(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `reconciliation_drilldown_${asOf}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Drill-down Excel ready — click any product code to open its ledger sheet");
    } catch { toast.error("Drill-down export failed"); }
    finally { setDrillExporting(false); }
  };

  const items = result?.items || [];
  const s = result?.summary || {};
  const filtered = useMemo(() => {
    let list = items;
    if (filter === "flagged") list = list.filter((i) => i.flagged);
    else if (filter === "variances") list = list.filter((i) => i.status === "erp_short" || i.status === "erp_excess");
    else if (filter !== "all") list = list.filter((i) => i.status === filter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((i) => (i.product_code + " " + (i.description || "")).toLowerCase().includes(q));
    return list;
  }, [items, filter, search]);

  const filters = [
    { k: "variances", label: `Variances (${s.variances || 0})` },
    { k: "flagged", label: `Flagged (${s.flagged || 0})` },
    { k: "erp_short", label: `ERP short (${s.erp_short || 0})` },
    { k: "erp_excess", label: `ERP excess (${s.erp_excess || 0})` },
    { k: "unmapped", label: `Unmapped (${s.unmapped || 0})` },
    { k: "not_counted", label: `Not counted (${s.not_counted || 0})` },
    { k: "match", label: `Matched (${s.matched || 0})` },
    { k: "all", label: `All (${s.total_products || 0})` },
  ];

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Inventory", to: "/safety/inventory" }, { label: "Reconcile" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Inventory", to: "/safety/inventory", depth: 1 },
        { label: "Reconcile", active: true, depth: 2 },
        { label: "Cutover Setup", to: "/safety/inventory/setup", depth: 2 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="inventory-reconcile-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center gap-3">
          <Scale className="w-5 h-5 text-emerald-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Inventory Reconciliation</h1>
            <p className="text-xs text-zinc-500">
              Upload your physical stock count, and the ERP tells you the cause of every difference vs its own inventory.
            </p>
          </div>
        </div>

        {/* Upload card */}
        <div className="sf-surface p-4 mb-4" data-testid="recon-upload-card">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium text-zinc-500 block mb-1">Physical stock file (.xlsx / .csv)</label>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-emerald-600 file:text-white file:text-sm hover:file:bg-emerald-700 cursor-pointer"
                data-testid="recon-file-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 block mb-1">Count as of (date &amp; time)</label>
              <Input type="datetime-local" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-56" data-testid="recon-asof-input" />
            </div>
            <Button onClick={runPreview} disabled={busy || !file} data-testid="recon-run-btn">
              <Upload className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
              Reconcile
            </Button>
            <Button variant="outline" onClick={openGroupsDialog} data-testid="recon-groups-btn-top">
              <Layers className="w-4 h-4 mr-1.5" /> Count-as-one groups
              {groups.length > 0 && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 border-indigo-200">{groups.length}</Badge>}
            </Button>
          </div>
          {file && <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1"><FileSpreadsheet className="w-3.5 h-3.5" /> {file.name}</p>}
        </div>

        {/* Results */}
        {result && (
          <>
            {result.parse_meta && (
              <p className="text-[11px] text-zinc-500 mb-3" data-testid="recon-parse-meta">
                Read sheet <b>{result.parse_meta.sheet}</b> · code column "<b>{result.parse_meta.code_column}</b>" · qty column "<b>{result.parse_meta.qty_column}</b>"
                {" · as of "}{fmtDate(result.as_of_date)}
                {!result.cutover_date && <span className="text-amber-600"> · ⚠ no active cutover baseline — ERP has no opening balance yet</span>}
              </p>
            )}

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
              <Kpi icon={Boxes} label="Products" value={fmt(s.total_products)} testid="recon-kpi-total" onClick={() => setFilter("all")} active={filter === "all"} />
              <Kpi icon={CheckCircle2} label="Matched" value={fmt(s.matched)} tone="emerald" testid="recon-kpi-matched" onClick={() => setFilter("match")} active={filter === "match"} />
              <Kpi icon={AlertTriangle} label="Variances" value={fmt(s.variances)} tone="amber" testid="recon-kpi-variances" onClick={() => setFilter("variances")} active={filter === "variances"} />
              <Kpi icon={HelpCircle} label="Unmapped" value={fmt(s.unmapped)} tone="violet" testid="recon-kpi-unmapped" onClick={() => setFilter("unmapped")} active={filter === "unmapped"} />
              <Kpi icon={Scale} label="Net variance" value={`${s.net_variance >= 0 ? "+" : ""}${fmt(s.net_variance)}`} tone={s.net_variance === 0 ? "zinc" : "rose"} testid="recon-kpi-net" />
              <Kpi icon={FileSpreadsheet} label="Physical / ERP" value={`${fmt(s.physical_total)} / ${fmt(s.erp_total)}`} tone="sky" testid="recon-kpi-totals" />
            </div>

            {/* Action bar */}
            <div className="sf-surface p-3 mb-3 flex flex-wrap items-center gap-2">
              {!reconId ? (
                <Button size="sm" onClick={save} disabled={saving} data-testid="recon-save-btn">
                  <Save className={`w-4 h-4 mr-1.5 ${saving ? "animate-spin" : ""}`} /> Save as draft
                </Button>
              ) : (
                <>
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="recon-saved-badge">Saved</Badge>
                  <Badge
                    className={status === "final" ? "bg-zinc-800 text-white" : "bg-amber-100 text-amber-700 border-amber-200"}
                    data-testid="recon-status-badge"
                  >
                    {status === "final" ? "Final" : "Draft"}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={toggleStatus} data-testid="recon-status-toggle">
                    {status === "final" ? <><Unlock className="w-4 h-4 mr-1.5" /> Reopen as draft</> : <><Lock className="w-4 h-4 mr-1.5" /> Mark final</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => trueUp([], true)} disabled={trueBusy || !s.variances} data-testid="recon-trueup-all-btn">
                    <Wand2 className={`w-4 h-4 mr-1.5 ${trueBusy ? "animate-spin" : ""}`} /> True-up all variances
                  </Button>
                  <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="recon-export-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button size="sm" variant="outline" onClick={exportDrilldownXlsx} disabled={drillExporting} data-testid="recon-export-drilldown-btn">
                    <Download className={`w-4 h-4 mr-1.5 ${drillExporting ? "animate-pulse" : ""}`} /> {drillExporting ? "Building…" : "Export with ledgers"}
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={openGroupsDialog} data-testid="recon-groups-btn">
                <Layers className="w-4 h-4 mr-1.5" /> Count-as-one groups
                {groups.length > 0 && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 border-indigo-200">{groups.length}</Badge>}
              </Button>
              <div className="ml-auto">
                <Input placeholder="Search product…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-52 h-9" data-testid="recon-search" />
              </div>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-2 mb-3">
              {filters.map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-emerald-400"}`}
                  data-testid={`recon-filter-${f.k}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="sf-surface overflow-x-auto" data-testid="recon-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 border-b">
                    <th className="p-3 w-6"></th>
                    <th className="p-3">Product</th>
                    <th className="p-3 text-right">Physical</th>
                    <th className="p-3 text-right">ERP</th>
                    <th className="p-3 text-right">Variance</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Cause of difference</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-zinc-400" data-testid="recon-empty">No products in this view.</td></tr>
                  )}
                  {filtered.map((it) => {
                    const st = STATUS[it.status] || STATUS.match;
                    const StIcon = st.icon;
                    const open = expanded[it.key];
                    return (
                      <Fragment key={it.key}>
                        <tr className={`border-b last:border-0 hover:bg-zinc-50 ${it.flagged ? "bg-red-50/60" : ""}`} data-testid={`recon-row-${it.product_code}`}>
                          <td className="p-3">
                            <button onClick={() => setExpanded((e) => ({ ...e, [it.key]: !e[it.key] }))} data-testid={`recon-expand-${it.product_code}`}>
                              {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setFlag(it, !it.flagged)}
                                title={it.flagged ? "Unflag" : "Flag for review"}
                                data-testid={`recon-flag-${it.product_code}`}
                              >
                                <Flag className={`w-4 h-4 ${it.flagged ? "text-red-600 fill-red-500" : "text-zinc-300 hover:text-zinc-500"}`} />
                              </button>
                              <div>
                                <div className="font-medium flex items-center gap-1.5">
                                  {it.product_code}
                                  {it.is_group && (
                                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px] gap-1" data-testid={`recon-group-badge-${it.product_code}`}>
                                      <Layers className="w-3 h-3" /> {(it.member_codes || []).length} codes
                                    </Badge>
                                  )}
                                </div>
                                {it.description && <div className="text-xs text-zinc-500">{it.description}</div>}
                              </div>
                              {it.note && <StickyNote className="w-3.5 h-3.5 text-amber-500" title={it.note} data-testid={`recon-note-icon-${it.product_code}`} />}
                            </div>
                          </td>
                          <td className="p-3 text-right tabular-nums">{fmt(it.physical_pieces)}</td>
                          <td className="p-3 text-right tabular-nums">{fmt(it.erp_pieces)}</td>
                          <td className={`p-3 text-right tabular-nums font-semibold ${it.variance > 0 ? "text-amber-600" : it.variance < 0 ? "text-rose-600" : "text-zinc-400"}`}>
                            {it.variance > 0 ? "+" : ""}{fmt(it.variance)}
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${st.cls}`}>
                              <StIcon className="w-3 h-3" /> {st.label}
                            </span>
                          </td>
                          <td className="p-3 text-xs text-zinc-600 max-w-[360px]">{it.cause}</td>
                          <td className="p-3 text-right">
                            {it.trued_up && reconId ? (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => undoTrueUp(it)} data-testid={`recon-undo-${it.product_code}`}>
                                <Undo2 className="w-3 h-3 mr-1" /> Undo true-up
                              </Button>
                            ) : (
                              <>
                                {it.can_true_up && reconId && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => trueUp([it.product_code])} disabled={trueBusy} data-testid={`recon-trueup-${it.product_code}`}>
                                    <Wand2 className="w-3 h-3 mr-1" /> True-up
                                  </Button>
                                )}
                                {it.status === "unmapped" && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openLinkDialog(it)} data-testid={`recon-link-${it.product_code}`}>
                                    <Link2 className="w-3 h-3 mr-1" /> Link to catalog
                                  </Button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-zinc-50/70" data-testid={`recon-detail-${it.product_code}`}>
                            <td></td>
                            <td colSpan={7} className="p-3">
                              <div className="flex flex-wrap gap-4 text-xs mb-3">
                                <span className="text-zinc-500">ERP breakdown:</span>
                                <span>Opening <b>{fmt(it.opening)}</b></span>
                                <span className="text-emerald-600">+ Produced <b>{fmt(it.produced)}</b></span>
                                <span className="text-rose-600">− Dispatched <b>{fmt(it.dispatched)}</b></span>
                                <span>± Adjustments <b>{it.adjustment > 0 ? "+" : ""}{fmt(it.adjustment)}</b></span>
                                <span className="text-zinc-700">= ERP <b>{fmt(it.erp_pieces)}</b></span>
                                <span className="text-zinc-700">vs Physical <b>{fmt(it.physical_pieces)}</b></span>
                              </div>
                              {it.is_group && (it.members || []).length > 0 && (
                                <div className="mb-3 border rounded-lg overflow-hidden" data-testid={`recon-group-members-${it.product_code}`}>
                                  <div className="bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 flex items-center gap-1.5">
                                    <Layers className="w-3.5 h-3.5" /> Counted as one — member product codes
                                  </div>
                                  <table className="w-full text-xs">
                                    <thead><tr className="text-zinc-500 border-b">
                                      <th className="p-2 text-left">Code</th>
                                      <th className="p-2 text-right">Physical</th>
                                      <th className="p-2 text-right">ERP</th>
                                      <th className="p-2 text-right">Ledger</th>
                                    </tr></thead>
                                    <tbody>
                                      {(it.members || []).map((m) => (
                                        <tr key={m.key} className="border-b last:border-0">
                                          <td className="p-2">{m.product_code}{it.primary_code === m.product_code && <span className="ml-1 text-indigo-600" title="True-up posts here">★</span>}</td>
                                          <td className="p-2 text-right tabular-nums">{fmt(m.physical_pieces)}</td>
                                          <td className="p-2 text-right tabular-nums">{fmt(m.erp_pieces)}</td>
                                          <td className="p-2 text-right">
                                            <button
                                              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                                              onClick={() => openLedger({ product_code: m.product_code, description: m.product_code })}
                                              data-testid={`recon-ledger-btn-${m.product_code}`}
                                            >
                                              <Receipt className="w-3.5 h-3.5" /> View
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <div className="px-3 py-2 border-t bg-white">
                                    <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => openGroupLedger(it)} data-testid={`recon-group-ledger-btn-${it.product_code}`}>
                                      <Receipt className="w-3.5 h-3.5 mr-1" /> Combined group ledger
                                    </Button>
                                  </div>
                                </div>
                              )}
                              <div className="mb-3">
                                {!it.is_group && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openLedger(it)} data-testid={`recon-ledger-btn-${it.product_code}`}>
                                    <Receipt className="w-3.5 h-3.5 mr-1" /> View detailed ledger
                                  </Button>
                                )}
                              </div>
                              <div className="flex items-start gap-3">
                                <Button
                                  size="sm"
                                  variant={it.flagged ? "default" : "outline"}
                                  className={`h-8 text-xs ${it.flagged ? "bg-red-600 hover:bg-red-700" : ""}`}
                                  onClick={() => setFlag(it, !it.flagged)}
                                  data-testid={`recon-flag-btn-${it.product_code}`}
                                >
                                  <Flag className="w-3.5 h-3.5 mr-1" /> {it.flagged ? "Flagged" : "Flag"}
                                </Button>
                                <div className="flex-1 flex items-start gap-2">
                                  <textarea
                                    className="flex-1 text-sm border rounded-md p-2 min-h-[38px] resize-y"
                                    placeholder="Add a note (e.g. recount pending, 1 pallet at dock, damaged stock)…"
                                    value={noteDraft[it.key] ?? it.note ?? ""}
                                    onChange={(e) => setNoteDraft((n) => ({ ...n, [it.key]: e.target.value }))}
                                    data-testid={`recon-note-input-${it.product_code}`}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={() => saveNote(it)}
                                    disabled={rowBusy[it.key]}
                                    data-testid={`recon-note-save-${it.product_code}`}
                                  >
                                    <StickyNote className="w-3.5 h-3.5 mr-1" /> Save note
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="sf-surface p-4 mt-6" data-testid="recon-history">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-zinc-500" />
              <h2 className="text-sm font-semibold">Past reconciliations</h2>
            </div>
            <div className="divide-y">
              {history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => openSaved(h.id)}
                  className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-zinc-50 rounded px-2"
                  data-testid={`recon-history-${h.id}`}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {h.name}
                      <Badge className={h.status === "final" ? "bg-zinc-800 text-white text-[10px]" : "bg-amber-100 text-amber-700 border-amber-200 text-[10px]"}>
                        {h.status === "final" ? "Final" : "Draft"}
                      </Badge>
                    </div>
                    <div className="text-xs text-zinc-500">as of {fmtDate(h.as_of_date)} · by {h.created_by}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">{(h.summary?.variances) || 0} variances</Badge>
                  <Badge variant="outline" className="text-xs">{(h.summary?.net_variance ?? 0) >= 0 ? "+" : ""}{fmt(h.summary?.net_variance)} net</Badge>
                  <ArrowRight className="w-4 h-4 text-zinc-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Count-as-one groups manager */}
        <Dialog open={groupsDialog} onOpenChange={(o) => { setGroupsDialog(o); if (!o) resetGroupForm(); }}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="recon-groups-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" /> Count-as-one groups</DialogTitle>
              <DialogDescription>
                Group different product codes that are the <b>same physical product</b> (e.g. <b>GR-10</b> + <b>RR-GR-10</b>, different brands).
                For reconciliation <b>only</b>, their ERP and physical counts are added together into one row — so counting them as one on the floor no longer shows a false “not counted”. This does <b>not</b> change your inventory, catalog, or aliases.
              </DialogDescription>
            </DialogHeader>

            {/* Suggested groups (auto-detected same-product code pairs) */}
            {suggestions.length > 0 && (
              <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3" data-testid="recon-suggestions">
                <div className="text-sm font-medium text-indigo-700 flex items-center gap-1.5 mb-2">
                  <Wand2 className="w-4 h-4" /> Suggested groups ({suggestions.length})
                  <span className="font-normal text-xs text-indigo-500">— codes that look like the same product under a different brand prefix</span>
                </div>
                <div className="space-y-1.5">
                  {suggestions.map((sug) => (
                    <div key={sug.base_code} className="flex items-center gap-2 bg-white rounded-md border border-indigo-100 px-2.5 py-1.5" data-testid={`recon-suggestion-${sug.base_code}`}>
                      <div className="min-w-0 flex-1 text-xs">
                        <span className="font-medium">{sug.base_code}</span>
                        <span className="text-zinc-500"> · {sug.members.map((m) => m.product_code).join(" + ")}</span>
                      </div>
                      <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" disabled={gBusy} onClick={() => createFromSuggestion(sug)} data-testid={`recon-suggestion-create-${sug.base_code}`}>
                        <Plus className="w-3 h-3 mr-1" /> Group
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {groups.length > 0 && (
              <div className="space-y-2 mb-3">
                {groups.map((g) => (
                  <div key={g.id} className="flex items-center gap-2 border rounded-lg p-2.5" data-testid={`recon-group-item-${g.id}`}>
                    <Layers className="w-4 h-4 text-indigo-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm">{g.name}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {(g.members || []).map((m) => m.product_code + (m.is_primary ? " ★" : "")).join(" · ")}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7" onClick={() => startEditGroup(g)} data-testid={`recon-group-edit-${g.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-rose-600" onClick={() => deleteGroup(g)} data-testid={`recon-group-delete-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}

            {/* Create / edit form */}
            <div className="border-t pt-3 space-y-3">
              <div className="text-sm font-medium flex items-center gap-1.5">
                {gEditing ? <>Editing “{gEditing.name}”</> : <><Plus className="w-4 h-4" /> New group</>}
              </div>
              <Input placeholder="Group name (e.g. GR-10 — all brands)" value={gName} onChange={(e) => setGName(e.target.value)} data-testid="recon-group-name" />

              {gMembers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {gMembers.map((pid) => {
                    const p = catalog.find((c) => c.id === pid);
                    const code = p?.product_code || pid;
                    return (
                      <span key={pid} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${gPrimary === pid ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-zinc-200"}`}>
                        <button title="Set as primary (true-up posts here)" onClick={() => setGPrimary(pid)} data-testid={`recon-group-primary-${code}`}>
                          <Star className={`w-3 h-3 ${gPrimary === pid ? "fill-white" : ""}`} />
                        </button>
                        {code}
                        <button onClick={() => toggleMember(pid)} data-testid={`recon-group-remove-${code}`}><X className="w-3 h-3" /></button>
                      </span>
                    );
                  })}
                </div>
              )}

              <Input placeholder="Search catalog to add products…" value={gSearch} onChange={(e) => setGSearch(e.target.value)} data-testid="recon-group-search" />
              <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                {gCatalogFiltered.map((p) => {
                  const on = gMembers.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggleMember(p.id)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50 ${on ? "bg-indigo-50" : ""}`}
                      data-testid={`recon-group-option-${p.product_code}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-indigo-600 border-indigo-600" : "border-zinc-300"}`}>
                        {on && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </span>
                      <span className="font-medium">{p.product_code}</span>
                      <span className="text-xs text-zinc-500 truncate">{p.description}</span>
                    </button>
                  );
                })}
                {gCatalogFiltered.length === 0 && <div className="px-3 py-4 text-center text-xs text-zinc-400">No matching products.</div>}
              </div>
            </div>

            <DialogFooter className="gap-2">
              {gEditing && <Button variant="ghost" onClick={resetGroupForm} data-testid="recon-group-cancel-edit">Cancel edit</Button>}
              <Button onClick={saveGroup} disabled={gBusy || gMembers.length < 2} data-testid="recon-group-save">
                {gEditing ? "Save changes" : "Create group"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Link unmapped → catalog product (add as alias) */}
        <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog((d) => ({ ...d, open: o }))}>
          <DialogContent className="max-w-lg" data-testid="recon-link-dialog">
            <DialogHeader>
              <DialogTitle>Link "{linkDialog.row?.product_code}" to a catalog product</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-zinc-500 -mt-2">
              This adds "{linkDialog.row?.product_code}" as an <b>alias</b> of the chosen product, so it maps to the catalog and reconciles cleanly from now on.
            </p>
            <div className="relative">
              <Search className="w-4 h-4 text-zinc-400 absolute left-2.5 top-2.5" />
              <Input
                autoFocus
                placeholder="Search product code or description…"
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                className="pl-8"
                data-testid="recon-link-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto divide-y border rounded-md">
              {catalog
                .filter((p) => {
                  const q = linkSearch.trim().toLowerCase();
                  if (!q) return true;
                  return ((p.product_code || "") + " " + (p.description || "")).toLowerCase().includes(q);
                })
                .slice(0, 60)
                .map((p) => (
                  <button
                    key={p.id || p.product_code}
                    onClick={() => submitLink(p)}
                    disabled={linkBusy}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-2"
                    data-testid={`recon-link-option-${p.product_code}`}
                  >
                    <div>
                      <div className="text-sm font-medium">{p.product_code}</div>
                      {p.description && <div className="text-xs text-zinc-500">{p.description}</div>}
                    </div>
                    <Link2 className="w-4 h-4 text-emerald-600" />
                  </button>
                ))}
              {catalog.length === 0 && <div className="p-4 text-center text-sm text-zinc-400">No catalog products loaded.</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialog({ open: false, row: null })} data-testid="recon-link-cancel">Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detailed product ledger (cross-reference deviations) */}
        <Dialog open={ledger.open} onOpenChange={(o) => setLedger((l) => ({ ...l, open: o }))}>
          <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="recon-ledger-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-600" />
                Ledger — {ledger.row?.product_code}
              </DialogTitle>
              <DialogDescription>
                Every ERP movement behind this product's stock, bounded to the reconciliation's as-of date, so you can trace where the deviation comes from.
              </DialogDescription>
            </DialogHeader>

            {ledger.loading ? (
              <div className="py-12 text-center text-sm text-zinc-400" data-testid="recon-ledger-loading">Loading ledger…</div>
            ) : ledger.data ? (
              <>
                {/* Context + reconciliation equation */}
                <div className="rounded-lg border bg-zinc-50 p-3 text-xs space-y-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-zinc-500">As of <b className="text-zinc-700">{fmtDate(ledger.data.as_of_date)}</b></span>
                    <span className="text-zinc-500">Cutover <b className="text-zinc-700">{fmtDate(ledger.data.cutover_date)}</b></span>
                    {ledger.row && (
                      <>
                        <span className="text-zinc-500">Physical <b className="text-zinc-700">{fmt(ledger.row.physical_pieces)}</b></span>
                        <span className={`font-semibold ${ledger.row.variance > 0 ? "text-amber-600" : ledger.row.variance < 0 ? "text-rose-600" : "text-zinc-500"}`}>
                          Variance {ledger.row.variance > 0 ? "+" : ""}{fmt(ledger.row.variance)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-zinc-600">
                    <span>Opening <b>{fmt(ledger.data.totals.opening_pieces)}</b></span>
                    <span className="text-emerald-600">+ Produced <b>{fmt(ledger.data.totals.produced_pieces)}</b></span>
                    <span className="text-rose-600">− Dispatched <b>{fmt(ledger.data.totals.dispatched_pieces)}</b></span>
                    <span>± Adjustments <b>{ledger.data.totals.adjustment_pieces > 0 ? "+" : ""}{fmt(ledger.data.totals.adjustment_pieces)}</b></span>
                    <span className="text-zinc-900">= ERP <b>{fmt(ledger.data.totals.erp_pieces)}</b></span>
                  </div>
                </div>

                {/* View toggle + export */}
                <div className="flex items-center justify-between mt-3">
                  <div className="inline-flex rounded-md border overflow-hidden text-xs">
                    <button
                      className={`px-3 py-1.5 flex items-center gap-1 ${ledger.view === "source" ? "bg-emerald-600 text-white" : "bg-white text-zinc-600"}`}
                      onClick={() => setLedger((l) => ({ ...l, view: "source" }))}
                      data-testid="recon-ledger-view-source"
                    ><Boxes className="w-3.5 h-3.5" /> By source</button>
                    <button
                      className={`px-3 py-1.5 flex items-center gap-1 ${ledger.view === "timeline" ? "bg-emerald-600 text-white" : "bg-white text-zinc-600"}`}
                      onClick={() => setLedger((l) => ({ ...l, view: "timeline" }))}
                      data-testid="recon-ledger-view-timeline"
                    ><ArrowRightLeft className="w-3.5 h-3.5" /> Timeline</button>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportLedgerCsv} data-testid="recon-ledger-export">
                    <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                  </Button>
                </div>

                {ledger.view === "timeline" ? (
                  <div className="mt-3 border rounded-md overflow-hidden">
                    <table className="w-full text-xs" data-testid="recon-ledger-timeline-table">
                      <thead className="bg-zinc-100 text-zinc-500">
                        <tr>
                          <th className="p-2 text-left">When</th>
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-left">Reference</th>
                          <th className="p-2 text-left">Detail</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.data.timeline.map((e, i) => (
                          <tr key={i} className="border-t" data-testid={`recon-ledger-tl-row-${i}`}>
                            <td className="p-2 whitespace-nowrap">{fmtDate(e.ts)}</td>
                            <td className="p-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                e.type === "PRODUCED" ? "bg-emerald-100 text-emerald-700" :
                                e.type === "DISPATCH" ? "bg-rose-100 text-rose-700" :
                                e.type === "ADJUSTMENT" ? "bg-amber-100 text-amber-700" : "bg-zinc-200 text-zinc-700"}`}>{e.type}</span>
                            </td>
                            <td className="p-2 font-mono">
                              {e.link_type && e.link_id ? (
                                <button
                                  className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                                  onClick={() => (e.link_type === "pallet" ? openPalletCard(e.link_id) : openPackingList(e.link_id))}
                                  data-testid={`recon-ledger-tl-link-${i}`}
                                >
                                  {e.ref ?? "—"} <ExternalLink className="w-3 h-3" />
                                </button>
                              ) : (e.ref ?? "—")}
                            </td>
                            <td className="p-2 text-zinc-500 max-w-[220px] truncate" title={e.detail}>
                              {e.type === "PRODUCED" && e.report_id ? (
                                <button className="text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={() => openReport(e.report_id)} data-testid={`recon-ledger-tl-dpr-${i}`}>
                                  {e.detail} <ExternalLink className="w-3 h-3" />
                                </button>
                              ) : e.detail}
                            </td>
                            <td className={`p-2 text-right tabular-nums font-medium ${e.qty > 0 ? "text-emerald-600" : e.qty < 0 ? "text-rose-600" : "text-zinc-400"}`}>{e.qty > 0 ? "+" : ""}{fmt(e.qty)}</td>
                            <td className="p-2 text-right tabular-nums font-semibold">{fmt(e.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-3 space-y-4" data-testid="recon-ledger-source">
                    <LedgerSection title="Opening (cutover baseline)" total={ledger.data.totals.opening_pieces} rows={ledger.data.opening}
                      cols={[["po_number", "PO"], ["customer_name", "Customer"], ["product_code", "Code"], ["qty_pieces", "Qty", true]]} empty="No opening balance recorded." />
                    <LedgerSection title="Produced (pallet scans)" total={ledger.data.totals.produced_pieces} tone="emerald" rows={ledger.data.produced.map((r) => ({ ...r, dpr: r.report ? `${r.report.report_no || r.report.report_date || ""} (${r.report.status || ""})` : "— not linked —" }))}
                      cols={[["produced_at", "When", false, "ts"], ["pallet_id", "Pallet"], ["state", "State"], ["po_number", "PO"], ["dpr", "DPR"], ["qty_pieces", "Qty", true]]} empty="No production scanned in this window."
                      linkers={{ pallet_id: (r) => (r.pallet_id ? () => openPalletCard(r.pallet_id) : null), dpr: (r) => (r.report_id ? () => openReport(r.report_id) : null) }} />
                    <LedgerSection title="Dispatched (packing lists)" total={ledger.data.totals.dispatched_pieces} tone="rose" sign="-" rows={ledger.data.dispatched}
                      cols={[["ts", "When"], ["invoice_no", "Invoice"], ["file_name", "File"], ["pallet_no", "Pallet"], ["po_number", "PO"], ["qty_pieces", "Qty", true]]} empty="No dispatches in this window."
                      linkers={{ invoice_no: (r) => (r.packing_list_id ? () => openPackingList(r.packing_list_id) : null), file_name: (r) => (r.packing_list_id ? () => openPackingList(r.packing_list_id) : null) }} />
                    <LedgerSection title="Adjustments (manual)" total={ledger.data.totals.adjustment_pieces} tone="amber" rows={ledger.data.adjustments}
                      cols={[["ts", "When"], ["reason", "Reason"], ["po_number", "PO"], ["created_by", "By"], ["qty_delta", "Delta", true]]} empty="No manual adjustments." />
                  </div>
                )}
              </>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setLedger((l) => ({ ...l, open: false }))} data-testid="recon-ledger-close">
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SafetyShell>
  );
}

function LedgerSection({ title, total, rows, cols, tone = "zinc", sign = "", empty, linkers }) {
  const toneCls = { zinc: "text-zinc-700", emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600" }[tone];
  const fmtN = (n) => new Intl.NumberFormat().format(n || 0);
  const cell = (r, c) => {
    const key = c[3] || c[0];
    let v = r[key];
    if (c[0] === "produced_at" || c[0] === "ts") return v ? fmtDate(v) : "—";
    return v == null || v === "" ? "—" : String(v);
  };
  return (
    <div className="border rounded-md overflow-hidden">
      <div className="flex items-center justify-between bg-zinc-50 px-3 py-2 border-b">
        <span className="text-xs font-semibold text-zinc-700">{title}</span>
        <span className={`text-xs font-semibold ${toneCls}`}>{sign}{fmtN(total)}</span>
      </div>
      {rows && rows.length ? (
        <table className="w-full text-xs">
          <thead className="text-zinc-400">
            <tr>{cols.map((c) => <th key={c[0]} className={`p-2 ${c[2] ? "text-right" : "text-left"} font-normal`}>{c[1]}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                {cols.map((c) => {
                  const linkFn = linkers && linkers[c[0]] ? linkers[c[0]](r) : null;
                  return (
                    <td key={c[0]} className={`p-2 ${c[2] ? "text-right tabular-nums font-medium" : "text-zinc-600"} ${["pallet_id", "invoice_no"].includes(c[0]) ? "font-mono" : ""}`}>
                      {c[2] ? `${sign}${fmtN(r[c[0]])}` : (linkFn ? (
                        <button className="text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={linkFn} data-testid={`recon-ledger-link-${c[0]}-${i}`}>
                          {cell(r, c)} <ExternalLink className="w-3 h-3" />
                        </button>
                      ) : cell(r, c))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="p-3 text-xs text-zinc-400">{empty}</div>
      )}
    </div>
  );
}
